// Render del pivote hora-a-hora a una imagen PNG descargable con la marca
// Jidokap, para que el supervisor la comparta en un grupo. Dibuja sobre un
// <canvas> nativo (sin dependencias: el stack es cerrado). El dato que se le
// pasa ya viene filtrado a SOLO VALIDADO desde el backend — acá no se decide
// qué se muestra.
//
// Layout (mockup 2026-07-02, versiones light y dark):
//   - Barra superior: wordmark "Jidokap." + planta · contexto + badge de turno
//   - Eyebrow "RESUMEN DE TURNO" + título serif "Producción hora a hora — {planta}"
//   - Línea meta: Turno N | HH:MM — HH:MM | Fecha | Líneas activas N
//   - Card KPI: PRODUCIDO | META DEL TURNO | % cumplimiento + barra de progreso
//     (solo si hay meta configurada; sin meta, solo PRODUCIDO)
//   - Tabla pivote (header oscuro, zebra, suma total con acento)
//   - Filas Meta y Cumplimiento por línea bajo la suma total (pedido UPG
//     2026-08-21) — solo si hay meta del turno; línea sin meta muestra «—»
//   - Pie: "✓ Solo información validada" + avatar + "Descargado por …" + wordmark

import type { SupervisorResumenHora, SupervisorResumenTurno } from 'schemas';
import { etiquetaUnidad } from '../../lib/unidades';

type Theme = 'light' | 'dark';

interface Opts {
  theme: Theme;
  /** Rol legible del que descarga ("Supervisor"). */
  rolLabel: string;
  /** Estado del turno para el badge; null → sin badge. */
  estadoTurno: 'cerrado' | 'en_curso' | null;
}

const PALETTES = {
  light: {
    pageBg: '#f6f3ee',
    surface: '#ffffff',
    border: '#e4e0d6',
    text: '#181621',
    muted: '#6d6a61',
    faint: '#9b968b',
    tableHeadBg: '#1b1b1f',
    tableHeadFg: '#f5f5f2',
    tableHeadMuted: '#b9b7ae',
    zebra: '#faf8f4',
    totalRowBg: '#efece5',
    accent: '#7a1e2e', // DN6
    ok: '#1a7f4e',
    warn: '#8c660b',
    crit: '#c8371f',
    barTrack: '#e7e4dc',
  },
  dark: {
    pageBg: '#141416',
    surface: '#1e1e21',
    border: '#2d2d31',
    text: '#f2f2f0',
    muted: '#a2a09a',
    faint: '#6f6d68',
    tableHeadBg: '#0b0b0d',
    tableHeadFg: '#f5f5f2',
    tableHeadMuted: '#8f8d86',
    zebra: '#19191c',
    totalRowBg: '#242428',
    accent: '#e06060',
    ok: '#3fc584',
    warn: '#e8b547',
    crit: '#ef6a5f',
    barTrack: '#333338',
  },
} as const;

// Las MISMAS fuentes de la plataforma (cargadas en index.html vía Google
// Fonts): Inter (texto), Instrument Serif (títulos), JetBrains Mono (números).
// Los fallbacks solo aplican si la webfont no cargó. Pesos disponibles:
// Inter 400/500/600 · Instrument Serif solo 400 · JetBrains Mono 400/500.
const SERIF = '"Instrument Serif", Georgia, serif';
const SANS = '"Inter", system-ui, -apple-system, "Segoe UI", sans-serif';
const MONO = '"JetBrains Mono", ui-monospace, Consolas, Menlo, monospace';

/**
 * Garantiza que las webfonts estén descargadas ANTES de dibujar: el canvas no
 * dispara la carga lazy de Google Fonts (display=swap) — sin esto, la primera
 * foto saldría con los fallbacks.
 */
async function cargarFuentes(): Promise<void> {
  try {
    await Promise.all([
      document.fonts.load(`34px ${SERIF}`),
      document.fonts.load(`600 13px ${SANS}`),
      document.fonts.load(`15px ${MONO}`),
      document.fonts.load(`500 15px ${MONO}`),
    ]);
  } catch {
    // Sin red o API no soportada → se dibuja con los fallbacks del stack.
  }
}

const fmt = (n: number | undefined) =>
  n === undefined || n === 0 ? '—' : n.toLocaleString('es-CO', { maximumFractionDigits: 0 });

type DrawCol =
  | { kind: 'franja'; label: string }
  | { kind: 'col'; label: string; key: string }
  | { kind: 'total'; label: string };

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

/** Wordmark "Jidokap." — serif, con el punto en acento. Devuelve el ancho. */
function drawWordmark(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  size: number,
  fg: string,
  accent: string,
): number {
  ctx.textAlign = 'left';
  ctx.font = `${size}px ${SERIF}`;
  ctx.fillStyle = fg;
  ctx.fillText('Jidokap', x, y);
  const w = ctx.measureText('Jidokap').width;
  ctx.fillStyle = accent;
  ctx.fillText('.', x + w, y);
  return w + ctx.measureText('.').width;
}

/** Construye el PNG con marca Jidokap y dispara la descarga. */
export async function descargarResumenImagen(
  data: SupervisorResumenHora,
  turno: SupervisorResumenTurno | undefined,
  opts: Opts,
): Promise<void> {
  await cargarFuentes();
  const P = PALETTES[opts.theme];
  const scale = 2;
  const padX = 40;
  const rowH = 44;
  const headerH = 46;
  const totalRowH = 50;
  const cellPadX = 18;
  const planta = data.plantas[0] ?? '';
  const lineasActivas = data.columnas.length;
  const producido = data.total_general;
  const unidad = etiquetaUnidad(data.unidad);
  const meta = data.meta_total && data.meta_total > 0 ? data.meta_total : null;
  const pct = meta ? Math.round((producido / meta) * 100) : null;
  // Umbrales compartidos entre la barra general y la fila Cumplimiento.
  const colorPct = (p: number) => (p >= 90 ? P.ok : p >= 70 ? P.warn : P.crit);
  const pctColor = pct === null ? P.muted : colorPct(pct);

  const cols: DrawCol[] = [
    { kind: 'franja', label: 'Hora a Hora' },
    ...data.columnas.map((c): DrawCol => ({ kind: 'col', label: c.nombre, key: c.key })),
    { kind: 'total', label: 'Suma total' },
  ];

  const cellText = (col: DrawCol, fila: SupervisorResumenHora['filas'][number]): string => {
    if (col.kind === 'franja') return fila.franja.replace(' - ', ' — ');
    if (col.kind === 'total') return fmt(fila.total);
    return fmt(fila.valores[col.key]);
  };
  const footerText = (col: DrawCol): string => {
    if (col.kind === 'franja') return 'Suma total';
    if (col.kind === 'total') return fmt(producido);
    return fmt(data.columnas.find((c) => c.key === col.key)?.total);
  };
  // Meta de la columna: 0 o null = sin meta configurada → «—» en ambas filas.
  const metaDeCol = (col: DrawCol): number | null => {
    if (col.kind === 'franja') return null;
    if (col.kind === 'total') return meta;
    const m = data.columnas.find((c) => c.key === col.key)?.meta;
    return m && m > 0 ? m : null;
  };
  const metaText = (col: DrawCol): string =>
    col.kind === 'franja' ? 'META' : fmt(metaDeCol(col) ?? undefined);
  const pctDeCol = (col: DrawCol): number | null => {
    const m = metaDeCol(col);
    if (!m) return null;
    const prodCol =
      col.kind === 'total'
        ? producido
        : col.kind === 'col'
          ? (data.columnas.find((c) => c.key === col.key)?.total ?? 0)
          : 0;
    return Math.round((prodCol / m) * 100);
  };
  const pctText = (col: DrawCol): string => {
    if (col.kind === 'franja') return 'CUMPLIMIENTO';
    const p = pctDeCol(col);
    return p === null ? '—' : `${p}%`;
  };

  // ── Medición de anchos ──
  const meas = document.createElement('canvas').getContext('2d')!;
  const headFont = `600 12px ${SANS}`;
  const cellFont = `15px ${MONO}`;
  const naturalWidths = cols.map((col) => {
    meas.font = headFont;
    let w = meas.measureText(col.label.toUpperCase()).width;
    meas.font = cellFont;
    for (const fila of data.filas) w = Math.max(w, meas.measureText(cellText(col, fila)).width);
    w = Math.max(w, meas.measureText(footerText(col)).width);
    if (meta) {
      w = Math.max(w, meas.measureText(metaText(col)).width);
      meas.font = `600 14px ${SANS}`;
      w = Math.max(w, meas.measureText(pctText(col)).width);
    }
    return Math.max(88, Math.ceil(w) + cellPadX * 2);
  });
  const naturalW = naturalWidths.reduce((a, b) => a + b, 0);
  const contentW = Math.max(naturalW, 980);
  // Estira las columnas proporcionalmente para ocupar todo el ancho.
  const stretch = contentW / naturalW;
  const widths = naturalWidths.map((w) => Math.floor(w * stretch));
  widths[widths.length - 1] = contentW - widths.slice(0, -1).reduce((a, b) => a + b, 0);

  // ── Layout vertical ──
  const canvasW = contentW + padX * 2;
  const topBarH = 64;
  const eyebrowY = topBarH + 44;
  const titleY = eyebrowY + 40;
  const metaRowY = titleY + 34;
  const cardH = meta ? 100 : 84;
  const cardY = metaRowY + 22;
  const tableY = cardY + cardH + 28;
  // Bloque Meta + Cumplimiento por línea: dos filas bajo la suma total,
  // separadas por una regla oscura. Solo existe si hay meta del turno.
  const metaRowH = 44;
  const metaBlockH = meta ? 2 + metaRowH * 2 : 0;
  const tableH = headerH + rowH * data.filas.length + totalRowH + metaBlockH;
  const footBarY = tableY + tableH + 28;
  const footBarH = 58;
  const canvasH = footBarY + footBarH;

  const canvas = document.createElement('canvas');
  canvas.width = canvasW * scale;
  canvas.height = canvasH * scale;
  const ctx = canvas.getContext('2d')!;
  ctx.scale(scale, scale);
  ctx.textBaseline = 'middle';

  // ── Fondo ──
  ctx.fillStyle = P.pageBg;
  ctx.fillRect(0, 0, canvasW, canvasH);

  // ── Barra superior ──
  ctx.fillStyle = P.surface;
  ctx.fillRect(0, 0, canvasW, topBarH);
  ctx.strokeStyle = P.border;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(0, topBarH + 0.5);
  ctx.lineTo(canvasW, topBarH + 0.5);
  ctx.stroke();

  const wmW = drawWordmark(ctx, padX, topBarH / 2, 24, P.text, P.accent);
  ctx.strokeStyle = P.border;
  ctx.beginPath();
  ctx.moveTo(padX + wmW + 16, topBarH / 2 - 12);
  ctx.lineTo(padX + wmW + 16, topBarH / 2 + 12);
  ctx.stroke();
  ctx.font = `14px ${SANS}`;
  ctx.fillStyle = P.muted;
  ctx.textAlign = 'left';
  ctx.fillText(`${planta} · Resumen del turno`, padX + wmW + 30, topBarH / 2);

  if (opts.estadoTurno) {
    const label = opts.estadoTurno === 'cerrado' ? 'TURNO CERRADO' : 'TURNO EN CURSO';
    ctx.font = `600 11px ${SANS}`;
    const tw = ctx.measureText(label).width;
    const bw = tw + 44;
    const bx = canvasW - padX - bw;
    roundRect(ctx, bx, topBarH / 2 - 14, bw, 28, 14);
    ctx.strokeStyle = P.border;
    ctx.stroke();
    ctx.fillStyle = P.ok;
    ctx.beginPath();
    ctx.arc(bx + 16, topBarH / 2, 3.5, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = P.text;
    ctx.textAlign = 'left';
    ctx.fillText(label, bx + 26, topBarH / 2 + 0.5);
  }

  // ── Eyebrow + título ──
  ctx.textAlign = 'left';
  ctx.font = `600 12px ${SANS}`;
  ctx.fillStyle = P.faint;
  ctx.fillText('R E S U M E N   D E   T U R N O', padX, eyebrowY);

  ctx.font = `34px ${SERIF}`;
  ctx.fillStyle = P.text;
  ctx.fillText('Producción hora a hora', padX, titleY);
  const t1w = ctx.measureText('Producción hora a hora').width;
  if (planta) {
    ctx.fillStyle = P.muted;
    ctx.fillText(' — ', padX + t1w, titleY);
    const t2w = ctx.measureText(' — ').width;
    ctx.fillStyle = P.accent;
    ctx.fillText(planta, padX + t1w + t2w, titleY);
  }

  // ── Línea meta (Turno N | horario | fecha | líneas activas) ──
  const metaParts: Array<[string, string]> = [];
  if (turno) {
    metaParts.push(['Turno ', String(turno.numero)]);
    metaParts.push(['', `${turno.hora_inicio} — ${turno.hora_fin}`]);
  }
  metaParts.push(['Fecha ', data.fecha]);
  metaParts.push(['Líneas activas ', String(lineasActivas)]);
  let mx = padX;
  metaParts.forEach(([label, value], i) => {
    if (i > 0) {
      ctx.strokeStyle = P.border;
      ctx.beginPath();
      ctx.moveTo(mx + 12, metaRowY - 8);
      ctx.lineTo(mx + 12, metaRowY + 8);
      ctx.stroke();
      mx += 24;
    }
    if (label) {
      ctx.font = `13px ${SANS}`;
      ctx.fillStyle = P.muted;
      ctx.fillText(label, mx, metaRowY);
      mx += ctx.measureText(label).width;
    }
    ctx.font = `500 13px ${MONO}`;
    ctx.fillStyle = P.text;
    ctx.fillText(value, mx, metaRowY);
    mx += ctx.measureText(value).width;
  });

  // ── Card KPI ──
  roundRect(ctx, padX, cardY, contentW, cardH, 10);
  ctx.fillStyle = P.surface;
  ctx.fill();
  ctx.strokeStyle = P.border;
  ctx.stroke();

  const cardMidY = cardY + cardH / 2;
  const stat = (x: number, label: string, value: string, unit: string): number => {
    ctx.textAlign = 'left';
    ctx.font = `600 11px ${SANS}`;
    ctx.fillStyle = P.faint;
    ctx.fillText(label, x, cardMidY - 16);
    ctx.font = `500 26px ${MONO}`;
    ctx.fillStyle = P.text;
    ctx.fillText(value, x, cardMidY + 12);
    const vw = ctx.measureText(value).width;
    if (unit) {
      ctx.font = `12px ${SANS}`;
      ctx.fillStyle = P.muted;
      ctx.fillText(unit, x + vw + 6, cardMidY + 14);
    }
    return x + Math.max(ctx.measureText(label).width, vw + 40) + 36;
  };

  let sx = padX + 28;
  sx = stat(sx, 'PRODUCIDO', fmt(producido), unidad);
  if (meta) {
    ctx.strokeStyle = P.border;
    ctx.beginPath();
    ctx.moveTo(sx - 18, cardY + 20);
    ctx.lineTo(sx - 18, cardY + cardH - 20);
    ctx.stroke();
    sx = stat(sx, 'META DEL TURNO', fmt(meta), unidad);

    // % + barra de progreso a la derecha
    ctx.font = `600 32px ${SANS}`;
    ctx.fillStyle = pctColor;
    ctx.textAlign = 'left';
    ctx.fillText(String(pct), sx, cardMidY - 4);
    const pw = ctx.measureText(String(pct)).width;
    ctx.font = `600 14px ${SANS}`;
    ctx.fillText('%', sx + pw + 2, cardMidY - 10);

    ctx.font = `12px ${SANS}`;
    ctx.fillStyle = P.muted;
    ctx.textAlign = 'right';
    ctx.fillText('Cumplimiento general del turno', padX + contentW - 28, cardMidY - 10);

    const barX = sx;
    const barW = padX + contentW - 28 - barX;
    const barY = cardMidY + 14;
    roundRect(ctx, barX, barY, barW, 8, 4);
    ctx.fillStyle = P.barTrack;
    ctx.fill();
    roundRect(ctx, barX, barY, Math.min(barW, barW * ((pct ?? 0) / 100)), 8, 4);
    ctx.fillStyle = pctColor;
    ctx.fill();
  }

  // ── Tabla ──
  const colX: number[] = [];
  let x = padX;
  for (const w of widths) {
    colX.push(x);
    x += w;
  }
  const drawCell = (
    col: DrawCol,
    ci: number,
    yMid: number,
    text: string,
    font: string,
    color: string,
  ) => {
    ctx.font = font;
    ctx.fillStyle = color;
    if (col.kind === 'franja') {
      ctx.textAlign = 'left';
      ctx.fillText(text, colX[ci]! + cellPadX, yMid);
    } else {
      ctx.textAlign = 'right';
      ctx.fillText(text, colX[ci]! + widths[ci]! - cellPadX, yMid);
    }
  };

  // Header oscuro
  roundRect(ctx, padX, tableY, contentW, headerH, 8);
  ctx.fillStyle = P.tableHeadBg;
  ctx.fill();
  ctx.fillRect(padX, tableY + headerH - 10, contentW, 10); // esquinas inferiores rectas
  cols.forEach((col, ci) => {
    const destacada = col.kind === 'franja' || col.kind === 'total';
    drawCell(
      col,
      ci,
      tableY + headerH / 2,
      col.label.toUpperCase(),
      headFont,
      destacada ? P.tableHeadFg : P.tableHeadMuted,
    );
  });

  // Filas con zebra
  data.filas.forEach((fila, ri) => {
    const y = tableY + headerH + rowH * ri;
    if (ri % 2 === 1) {
      ctx.fillStyle = P.zebra;
      ctx.fillRect(padX, y, contentW, rowH);
    }
    ctx.strokeStyle = P.border;
    ctx.beginPath();
    ctx.moveTo(padX, y + rowH + 0.5);
    ctx.lineTo(padX + contentW, y + rowH + 0.5);
    ctx.stroke();
    cols.forEach((col, ci) => {
      const esFranja = col.kind === 'franja';
      drawCell(
        col,
        ci,
        y + rowH / 2,
        cellText(col, fila),
        esFranja ? `600 14px ${SANS}` : cellFont,
        esFranja ? P.muted : P.text,
      );
    });
  });

  // Fila Suma total
  const totalY = tableY + headerH + rowH * data.filas.length;
  ctx.fillStyle = P.totalRowBg;
  ctx.fillRect(padX, totalY, contentW, totalRowH);
  cols.forEach((col, ci) => {
    const esGranTotal = col.kind === 'total';
    drawCell(
      col,
      ci,
      totalY + totalRowH / 2,
      footerText(col),
      col.kind === 'franja' ? `600 14px ${SANS}` : `500 15px ${MONO}`,
      esGranTotal ? P.accent : P.text,
    );
  });

  // Filas Meta + Cumplimiento por línea (solo con meta configurada)
  if (meta) {
    ctx.fillStyle = P.tableHeadBg;
    ctx.fillRect(padX, totalY + totalRowH, contentW, 2);
    const metaY = totalY + totalRowH + 2;
    ctx.fillStyle = P.surface;
    ctx.fillRect(padX, metaY, contentW, metaRowH * 2);

    const labelFont = `600 11px ${SANS}`;
    cols.forEach((col, ci) => {
      const esFranja = col.kind === 'franja';
      drawCell(
        col,
        ci,
        metaY + metaRowH / 2,
        metaText(col),
        esFranja ? labelFont : cellFont,
        esFranja ? P.faint : P.muted,
      );
    });
    cols.forEach((col, ci) => {
      const esFranja = col.kind === 'franja';
      const p = pctDeCol(col);
      drawCell(
        col,
        ci,
        metaY + metaRowH + metaRowH / 2,
        pctText(col),
        esFranja ? labelFont : `600 14px ${SANS}`,
        esFranja ? P.faint : p === null ? P.muted : colorPct(p),
      );
    });
  }

  // ── Pie ──
  ctx.fillStyle = P.surface;
  ctx.fillRect(0, footBarY, canvasW, footBarH);
  ctx.strokeStyle = P.border;
  ctx.beginPath();
  ctx.moveTo(0, footBarY + 0.5);
  ctx.lineTo(canvasW, footBarY + 0.5);
  ctx.stroke();

  const footMidY = footBarY + footBarH / 2;
  ctx.textAlign = 'left';
  ctx.font = `600 13px ${SANS}`;
  ctx.fillStyle = P.ok;
  ctx.fillText('✓', padX, footMidY);
  ctx.fillStyle = P.text;
  ctx.fillText('Solo información validada', padX + 18, footMidY);
  let fx = padX + 18 + ctx.measureText('Solo información validada').width + 22;

  // Avatar con iniciales
  const nombre = data.supervisor_nombre || '—';
  const iniciales = nombre
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]!.toUpperCase())
    .join('');
  ctx.fillStyle = P.zebra;
  ctx.beginPath();
  ctx.arc(fx + 13, footMidY, 13, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = P.border;
  ctx.stroke();
  ctx.font = `600 10px ${SANS}`;
  ctx.fillStyle = P.muted;
  ctx.textAlign = 'center';
  ctx.fillText(iniciales, fx + 13, footMidY + 0.5);
  fx += 34;

  const generado = new Date().toLocaleString('es-CO', { dateStyle: 'short', timeStyle: 'short' });
  ctx.textAlign = 'left';
  ctx.font = `13px ${SANS}`;
  ctx.fillStyle = P.muted;
  ctx.fillText('Descargado por ', fx, footMidY);
  fx += ctx.measureText('Descargado por ').width;
  ctx.font = `600 13px ${SANS}`;
  ctx.fillStyle = P.text;
  ctx.fillText(nombre, fx, footMidY);
  fx += ctx.measureText(nombre).width;
  ctx.font = `13px ${SANS}`;
  ctx.fillStyle = P.muted;
  ctx.fillText(` · ${opts.rolLabel} · ${generado}`, fx, footMidY);

  ctx.textAlign = 'right';
  ctx.save();
  ctx.textAlign = 'left';
  ctx.font = `15px ${SERIF}`;
  const wmSmallW = ctx.measureText('Jidokap.').width;
  drawWordmark(ctx, canvasW - padX - wmSmallW, footMidY, 15, P.muted, P.accent);
  ctx.restore();

  // ── Descarga ──
  canvas.toBlob((blob) => {
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `resumen-turno-${data.fecha}${turno ? `-T${turno.numero}` : ''}.png`;
    a.click();
    URL.revokeObjectURL(url);
  }, 'image/png');
}
